import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, BellRing, RefreshCw, Mail, MailWarning } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { adminGet, reportError, formatDate } from './adminApi';

/** Pointages written through the REST API (Dolibarr button bypassed) + "fin oubliée" reminders sent to Easers. */
export function AdminPointageAlerts({ adminKey }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/easer/alerts', adminKey);
      setData(res.data);
    } catch (err) {
      reportError('alertes pointage', err, (m) => toast.error(m || 'Chargement impossible'));
      setData((d) => d || { api_fallbacks: [], reminders: [] });
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Skeleton className="h-28 rounded-xl" />;
  const Sent = ({ ok, error }) => ok
    ? <Badge variant="outline" className="gap-1"><Mail className="h-3 w-3" /> e-mail envoyé</Badge>
    : <Badge variant="outline" className="gap-1 border-destructive/30 text-destructive" title={error}><MailWarning className="h-3 w-3" /> e-mail non envoyé</Badge>;

  return (
    <Card className="p-5" data-testid="admin-pointage-alerts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Alertes pointage</h3>
        <Button variant="outline" size="sm" onClick={load} data-testid="admin-pointage-alerts-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Actualiser</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-3">Un e-mail est envoyé à l'agence chaque fois qu'un pointage n'a pas pu passer par le bouton Dolibarr (heures à contrôler dans MySchedule), et à l'Easer quand une prestation reste ouverte 30 min après sa fin prévue (vérification toutes les 15 min).</p>

      <h4 className="text-sm font-semibold mb-2">Pointages hors bouton Dolibarr ({data.api_fallbacks.length})</h4>
      {data.api_fallbacks.length === 0 ? <p className="text-sm text-muted-foreground mb-4">Aucun : tous les pointages sont passés par Dolibarr.</p> : (
        <div className="space-y-2 mb-4">
          {data.api_fallbacks.slice(0, 20).map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid="admin-pointage-alert-row">
              <Badge variant="outline">{a.action === 'start' ? 'DEBUT' : 'FIN'}</Badge>
              <span className="font-medium notranslate">{a.user_name}</span>
              <span className="text-muted-foreground">{a.intervention_ref} · {a.recorded_for} · {a.verified ? 'vérifié' : 'NON vérifié'}</span>
              <span className="text-xs text-muted-foreground truncate max-w-full">{a.reason}</span>
              <span className="ml-auto"><Sent ok={a.email_sent} /></span>
            </div>
          ))}
        </div>
      )}

      <h4 className="text-sm font-semibold mb-2 flex items-center gap-1"><BellRing className="h-4 w-4" /> Rappels « fin de prestation oubliée » ({data.reminders.length})</h4>
      {data.reminders.length === 0 ? <p className="text-sm text-muted-foreground">Aucun rappel envoyé.</p> : (
        <div className="space-y-2">
          {data.reminders.slice(0, 20).map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid="admin-reminder-row">
              <span className="font-medium notranslate">{r.user_name}</span>
              <span className="text-muted-foreground">{r.label} · fin prévue {formatDate(r.planned_end, true)} · rappel {formatDate(r.sent_at, true)}</span>
              <span className="ml-auto"><Sent ok={r.email_sent} error={r.email_error} /></span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
